import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import {
  fetchSupportMeta,
  fetchThread,
  fetchTickets,
  fetchUnreadCount,
  openTicket,
  replyToTicket,
  setTicketStatus,
  type SupportFilters,
} from './api';

export const supportKeys = {
  all: ['support'] as const,
  list: (filters: SupportFilters) => [...supportKeys.all, 'list', filters] as const,
  thread: (reference: string) => [...supportKeys.all, 'thread', reference] as const,
  meta: () => [...supportKeys.all, 'meta'] as const,
  unread: () => [...supportKeys.all, 'unread'] as const,
};

export function useSupportTickets(filters: SupportFilters) {
  return useQuery({
    queryKey: supportKeys.list(filters),
    queryFn: () => fetchTickets(filters),
    placeholderData: (previous) => previous,
  });
}

export function useSupportThread(reference: string | undefined) {
  return useQuery({
    queryKey: supportKeys.thread(reference ?? ''),
    queryFn: () => fetchThread(reference as string),
    enabled: reference !== undefined,
  });
}

export function useSupportMeta() {
  return useQuery({
    queryKey: supportKeys.meta(),
    queryFn: fetchSupportMeta,
    // Categories and priorities come from a PHP enum. They change on deploy,
    // not during a shift.
    staleTime: Infinity,
  });
}

/**
 * The sidebar badge.
 *
 * Polled, because support is the one place in this app where the other party
 * writes without the customer doing anything, and a shop that has to reload to
 * discover we answered is a shop that finds out tomorrow. Two minutes is slow
 * enough to be invisible on a phone tether and fast enough to matter.
 *
 * Only runs when somebody can actually see support — the query is disabled
 * otherwise rather than firing and 403ing every two minutes.
 */
export function useSupportUnreadCount() {
  const session = useAuthStore((s) => s.session);

  const allowed =
    session?.user.is_owner === true ||
    (session?.capabilities ?? []).some((c) => c === 'support.view' || c === 'support.manage');

  return useQuery({
    queryKey: supportKeys.unread(),
    queryFn: fetchUnreadCount,
    enabled: allowed,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });
}

export function useOpenTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: openTicket,
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.all });
      // The reference, because it is what they will quote back to us.
      toast.success(`${ticket.reference} raised — we'll reply by email too`);
    },
  });
}

export function useReplyToTicket(reference: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body, attachments }: { body: string; attachments?: File[] }) =>
      replyToTicket(reference, body, attachments ?? []),
    onSuccess: (thread) => {
      // The response IS the fresh thread, so write it straight in — a refetch
      // would blank the conversation for a moment after every reply.
      queryClient.setQueryData(supportKeys.thread(reference), thread);
      void queryClient.invalidateQueries({ queryKey: supportKeys.unread() });
    },
  });
}

export function useSetTicketStatus(reference: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: 'closed' | 'open') => setTicketStatus(reference, status),
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.all });
      toast.success(ticket.is_open ? 'Reopened' : 'Closed — thanks');
    },
  });
}
